import {
  BarRenderer,
  type BarRendererStyle,
} from "./bar-renderer";
import { BucketsRenderer } from "./buckets-renderer";
import {
  CandlestickRenderer,
  type CandlestickRendererStyle,
} from "./candlestick-renderer";
import {
  FunnelRenderer,
  type FunnelRendererStyle,
} from "./funnel-renderer";
import {
  HRuleAllRenderer,
  HRuleRenderer,
  type RuleOffsetType,
} from "./hrule-renderer";
import { LineRenderer } from "./line-renderer";
import type { CanvasSeriesRenderer } from "./renderer";
import type { PlotCollection, PlotRequest } from "@tento-data";
import type { CanvasRenderTarget } from "../canvas/canvas-types";
import {
  RegressionBandRenderer,
  RegressionCurveRenderer,
  RegressionFillRenderer,
  RegressionPointsRenderer,
} from "./regression-renderers";
import { ScatterRenderer } from "./scatter-renderer";

export type RendererAttributeValue = string | number | boolean | null;
export type RendererAttributes = Readonly<Record<string, RendererAttributeValue>>;

/**
 * Converts data-renderer attributes into the new CanvasSeriesRenderer set.
 *
 * `attrs.type` is the mark KIND (`points`, `line`, `bar`, …), NOT a wire
 * `_type`. Callers derive it from the row's wire type or legacy attrs
 * (`@tento-lona/sheets` `rendererKindOf`) and pass it in here.
 */
/**
 * Wraps a renderer so it paints ONLY the series whose `descriptor.key` matches
 * the key it was built for. Applied by `createSeriesRenderer` whenever a
 * renderer's attrs carry a `key`, so a multi-series chart (each series a
 * distinct keyed line/points mark) no longer bleeds every series into every
 * renderer's color. Renderers built WITHOUT a key stay unscoped and keep
 * painting every supported entry — backward compatible with single-series
 * presets and chart-wide marks (e.g. `hrule`, `hrule-all`) that intentionally
 * span all series.
 */
class KeyScopedRenderer implements CanvasSeriesRenderer {
  constructor(
    private readonly inner: CanvasSeriesRenderer,
    private readonly key: string,
  ) {}

  supports(entry: PlotCollection.AnyEntry): boolean {
    return entry.descriptor.key === this.key && this.inner.supports(entry);
  }

  render(
    target: CanvasRenderTarget,
    request: PlotRequest,
    entry: PlotCollection.AnyEntry,
  ): void {
    if (entry.descriptor.key !== this.key) return;
    this.inner.render(target, request, entry);
  }
}

export function createSeriesRenderer(
  attrs: RendererAttributes,
): Option<CanvasSeriesRenderer> {
  const base = createBaseRenderer(attrs);
  if (base == null) return null;
  // A renderer carrying a `key` scopes to the matching series; a keyless one
  // paints every supported entry (single-series + chart-wide marks).
  const key = str(attrs, "key", "");
  return key.length > 0 ? new KeyScopedRenderer(base, key) : base;
}

function createBaseRenderer(
  attrs: RendererAttributes,
): Option<CanvasSeriesRenderer> {
  const type = str(attrs, "type", "");
  if (type.length === 0) return null;

  switch (type) {
    case "line":
      return new LineRenderer({
        strokeStyle: str(attrs, "color", "#2563eb"),
        lineWidth: num(attrs, "lineWidth", 2),
      });

    case "points":
    case "scatter":
      return new ScatterRenderer({
        fillStyle: str(attrs, "color", "#2563eb"),
        radius: num(attrs, "radius", 3),
      });

    case "bar":
    case "bar-chart-with-width":
      return new BarRenderer(barStyle(attrs));

    case "candlestick":
      return new CandlestickRenderer(candlestickStyle(attrs));

    case "funnel":
      return new FunnelRenderer(funnelStyle(attrs));

    case "hrule":
      return new HRuleRenderer({
        offset: num(attrs, "offset", 0),
        offsetType: offsetType(attrs),
        center: optNum(attrs, "center"),
        strokeStyle: str(attrs, "color", "#d1d5db"),
        lineWidth: num(attrs, "lineWidth", 1),
      });

    case "hrule-all":
      return new HRuleAllRenderer({
        offset: num(attrs, "offset", 0),
        offsetType: offsetType(attrs),
        center: optNum(attrs, "center"),
        strokeStyle: str(attrs, "color", "#d1d5db"),
        lineWidth: num(attrs, "lineWidth", 1),
      });

    case "regression-curve":
      return new RegressionCurveRenderer({
        strokeStyle: str(attrs, "color", "#666666"),
        lineWidth: num(attrs, "lineWidth", 2),
      });

    case "regression-band":
      return new RegressionBandRenderer({
        fillStyle: str(attrs, "color", "rgba(245, 158, 11, 0.1)"),
      });

    case "regression-fill":
      return new RegressionFillRenderer({
        offset: num(attrs, "offset", 0),
        offsetType: offsetType(attrs),
        center: optNum(attrs, "center"),
        topFillStyle: str(attrs, "topColor", "rgba(239, 68, 68, 0.3)"),
        bottomFillStyle: str(attrs, "bottomColor", "rgba(59, 130, 246, 0.3)"),
      });

    case "regression-points":
      return new RegressionPointsRenderer({
        fillStyle: str(attrs, "color", "rgb(255, 0, 0)"),
        radius: num(attrs, "radius", 2),
      });

    case "buckets":
      return new BucketsRenderer({
        fillStyle: str(attrs, "barColor", str(attrs, "color", "#2563eb")),
        bucketCount: num(attrs, "bucketCount", 48),
        minWidth: num(attrs, "minWidth", 1),
      });

    default:
      return null;
  }
}

function str(attrs: RendererAttributes, key: string, fallback: string): string {
  const value = attrs[key];
  return typeof value === "string" ? value : fallback;
}

function num(attrs: RendererAttributes, key: string, fallback: number): number {
  const value = attrs[key];
  return typeof value === "number" ? value : fallback;
}

function csv(attrs: RendererAttributes, key: string): readonly string[] {
  const value = attrs[key];
  if (typeof value !== "string") return [];
  return value.split(",").map((part) => part.trim()).filter((part) => part.length > 0);
}

function optNum(attrs: RendererAttributes, key: string): Option<number> {
  const value = attrs[key];
  return typeof value === "number" ? value : null;
}

function offsetType(attrs: RendererAttributes): RuleOffsetType {
  return attrs["offsetType"] === "abs" ? "abs" : "pct";
}

function barStyle(attrs: RendererAttributes): BarRendererStyle {
  return {
    fillStyle: str(attrs, "barColor", str(attrs, "color", "#2563eb")),
    minWidth: num(attrs, "minWidth", 4),
  };
}

function funnelStyle(attrs: RendererAttributes): FunnelRendererStyle {
  return {
    fillStyle: str(attrs, "color", "#999cff"),
    stages: csv(attrs, "stages"),
    minBarPx: num(attrs, "minBarPx", 2),
    ghostAlpha: num(attrs, "ghostAlpha", 0.18),
  };
}

function candlestickStyle(attrs: RendererAttributes): CandlestickRendererStyle {
  return {
    risingFillStyle: str(attrs, "risingFillStyle", "#16a34a"),
    fallingFillStyle: str(attrs, "fallingFillStyle", "#dc2626"),
    wickStrokeStyle: str(attrs, "wickStrokeStyle", "#111827"),
    lineWidth: num(attrs, "lineWidth", 1),
    maxBodyWidth: num(attrs, "maxBodyWidth", 12),
    slotPx: num(attrs, "slotPx", 9),
  };
}
